/* eslint-disable no-debugger */
import { AxiosResponse } from "axios";

import {
  IAiSummaryResponse,
  ICreateNoteProps,
  ICreateNoteWithImagesProps,
  IEditNoteProps,
  IEditNoteWithImagesProps,
  IImageReference,
  INoteWithImageMappingResponse,
} from "../@types/Note";
import { IPaginatedResponse } from "../@types/PaginatedResponse";
import { api } from "./api";

export const noteService = {
  async getNotes(patientId: number): Promise<AxiosResponse> {
    const url = "/Portal/Notes";

    const urlParamsParsed = new URLSearchParams();

    urlParamsParsed.append("patientId", patientId.toString());

    const notesResponse = await api.get<IPaginatedResponse<any>>(url, {
      params: urlParamsParsed,
    });

    return notesResponse;
  },

  async getNotesPaginated(
    patientId: number,
    page?: number,
    pageSize?: number
  ): Promise<AxiosResponse<IPaginatedResponse<any>>> {
    const url = "/Portal/Notes";

    const params: Record<string, string | number> = {
      patientId,
    };

    if (page !== undefined) {
      params.page = page;
    }
    if (pageSize !== undefined) {
      params.pageSize = pageSize;
    }

    return api.get<IPaginatedResponse<any>>(url, { params });
  },

  async getNoteById(id: number): Promise<AxiosResponse> {
    const url = `/Portal/Notes/${id}`;
    const note = await api.get(url);
    return note;
  },

  // JSON create/edit — the standard path used by the Portal. Server routes by
  // Content-Type, so the URL is the same as the multipart variants below.
  async createNote(createNoteProps: ICreateNoteProps): Promise<AxiosResponse> {
    const url = `/Portal/Notes/`;
    const time = new Date().toISOString();
    const { noteContent, patientId, taggedHcpIds, assignedHcpId, isUrgent } =
      createNoteProps;

    const response = await api.post(url, {
      time,
      noteContent,
      patientId,
      taggedHcpIds,
      assignedHcpId: assignedHcpId ?? null,
      isUrgent: isUrgent ?? false,
    });
    return response;
  },

  async editNote(
    id: number,
    editNoteProps: IEditNoteProps
  ): Promise<AxiosResponse> {
    const url = `/Portal/Notes/${id}`;
    const { noteContent, taggedHcpIds, assignedHcpId, isUrgent } =
      editNoteProps;

    const response = await api.put(url, {
      noteContent,
      taggedHcpIds,
      assignedHcpId: assignedHcpId ?? null,
      isUrgent: isUrgent ?? false,
    });
    return response;
  },

  /**
   * Multipart create — POC variant that uploads images alongside the note in
   * a single request. Kept side-by-side with `createNote` for the in-flight
   * images/AI-summary POC; not consumed by the Portal today.
   */
  async createNoteWithImages(
    props: ICreateNoteWithImagesProps
  ): Promise<INoteWithImageMappingResponse> {
    const url = `/Portal/Notes/`;
    const time = new Date().toISOString();
    const { noteContent, patientId, images, taggedHcpIds } = props;

    const formData = new FormData();
    formData.append("noteContent", noteContent);
    formData.append("time", time);
    formData.append("patientId", patientId.toString());

    if (images && images.length > 0) {
      images.forEach((image) => {
        formData.append("images", image);
      });
    }

    // Append tagged HCP IDs as repeated form fields so the server reads them
    // through Request.Form["taggedHcpIds"] as a multi-value entry.
    if (taggedHcpIds && taggedHcpIds.length > 0) {
      taggedHcpIds.forEach((id) => {
        formData.append("taggedHcpIds", id.toString());
      });
    }

    const response = await api.post<INoteWithImageMappingResponse>(
      url,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  },

  /**
   * Multipart edit — POC counterpart to `editNote`. Same URL, multipart body.
   */
  async editNoteWithImages(
    noteId: number,
    props: IEditNoteWithImagesProps
  ): Promise<INoteWithImageMappingResponse> {
    const url = `/Portal/Notes/${noteId}`;
    const { noteContent, newImages, taggedHcpIds } = props;

    const formData = new FormData();
    formData.append("noteContent", noteContent);

    if (newImages && newImages.length > 0) {
      newImages.forEach((image) => {
        formData.append("newImages", image);
      });
    }

    if (taggedHcpIds && taggedHcpIds.length > 0) {
      taggedHcpIds.forEach((id) => {
        formData.append("taggedHcpIds", id.toString());
      });
    }

    const response = await api.put<INoteWithImageMappingResponse>(
      url,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  },

  async deleteNote(id: number): Promise<AxiosResponse> {
    const url = `/Portal/Notes/${id}`;

    const response = await api.patch(url, { publish: false });
    return response;
  },

  async setUrgent(id: number, isUrgent: boolean): Promise<AxiosResponse> {
    const url = `/Portal/Notes/${id}/Urgent`;
    return api.patch(url, { isUrgent });
  },

  async setCompleted(id: number, isCompleted: boolean): Promise<AxiosResponse> {
    const url = `/Portal/Notes/${id}/Complete`;
    return api.patch(url, { isCompleted });
  },

  async getMyAssignedTasks(
    page?: number,
    pageSize?: number
  ): Promise<AxiosResponse<IPaginatedResponse<any>>> {
    const url = "/Portal/Notes/MyTasks";

    const params: Record<string, string | number> = {};
    if (page !== undefined) params.page = page;
    if (pageSize !== undefined) params.pageSize = pageSize;

    return api.get<IPaginatedResponse<any>>(url, { params });
  },

  async getNoteImages(noteId: number): Promise<IImageReference[]> {
    const url = `/Portal/Notes/${noteId}/Images`;
    const response = await api.get<{ images: IImageReference[] }>(url);
    return response.data.images;
  },

  /**
   * Generate AI summary for an existing note
   */
  async generateAiSummary(noteId: number): Promise<IAiSummaryResponse> {
    const url = `/Portal/Notes/${noteId}/GenerateAiSummary`;
    const response = await api.post<IAiSummaryResponse>(url);
    return response.data;
  },
};
